import { addError } from 'tio-common/utils/datadog';
import { getOwner } from '@ember/application';
import { assert } from '@ember/debug';
import ENV from 'tio-employee/config/environment';
import RESTAdapter, { type RequestData } from '@ember-data/adapter/rest';
import type SessionService from '../services/session';
import type SessionContextService from '../services/session-context';
import downloadFile from 'tio-common/utils/download-file';
import { timeZone } from 'tio-common/utils/date/time-zone';
import { generateRequestId } from 'tio-common/utils/request-id';
import type { ReportExportResponse } from 'tio-data/models/search';

/**
 *
 * @returns {object} The server response.
 */
export default class SearchModelAdapter extends RESTAdapter {
  declare ajax: RESTAdapter['ajax'];
  declare buildURL: (...args: unknown[]) => string;

  get session(): SessionService {
    const owner = getOwner(this);
    assert('Expected owner to be available for adapter', owner);

    const session = owner.lookup('service:session');
    assert('Expected service:session to be registered', session);
    return session as SessionService;
  }

  get sessionContext(): SessionContextService {
    const owner = getOwner(this);
    assert('Expected owner to be available for adapter', owner);

    const sessionContext = owner.lookup('service:session-context');
    assert('Expected service:session-context to be registered', sessionContext);
    return sessionContext as SessionContextService;
  }

  namespace = 'api/v2';
  host = ENV.apiHost;
  // @ts-expect-error stop using adapters
  get headers() {
    const headers: Record<string, unknown> = {
      'x-api-key': ENV.apiKey,
      'Content-Type': 'application/json;',
      'Time-Zone': timeZone(),
      'X-Request-Id': generateRequestId(),
    };

    if (this.session.publicKey) {
      headers['x-auth-key'] = this.session.publicKey;
    }

    if (this.session.isAuthenticated) {
      headers['tio-auth-token'] = `${this.session.data.authenticated?.access_token}`;
    }

    return headers;
  }

  async search(query: Record<string, unknown>) {
    const url = this.buildURL('search');
    return this.ajax(url, 'POST', { data: query });
  }

  async mapping(modelType: string) {
    const url = this.buildURL('search') + '/mapping?model-type=' + modelType;
    return this.ajax(url, 'GET');
  }

  async mappingWithLabelMaps(modelType: string, companyId?: string, programTemplateId?: string) {
    const params = new URLSearchParams({
      'model-type': modelType,
      'include-label-maps': 'true',
    });
    if (companyId) {
      params.set('company-id', companyId);
    }
    if (programTemplateId) {
      params.set('program-template-id', programTemplateId);
    }

    const url = this.buildURL('search') + `/mapping?${params.toString()}`;
    return this.ajax(url, 'GET');
  }

  async fields(modelType: string, companyId?: string, programTemplateId?: string) {
    const params = new URLSearchParams({ 'model-type': modelType });
    if (companyId) {
      params.set('company-id', companyId);
    }
    if (programTemplateId) {
      params.set('program-template-id', programTemplateId);
    }

    const url = this.buildURL('search') + `/fields?${params.toString()}`;
    return this.ajax(url, 'GET');
  }

  async employeeCustomFieldMappings(companyId: string, programTemplateId?: string) {
    const params = new URLSearchParams({ 'company-id': companyId });
    if (programTemplateId) {
      params.set('program-template-id', programTemplateId);
    }

    const url = this.buildURL('search') + `/employee-custom-field-mappings?${params.toString()}`;
    return this.ajax(url, 'GET');
  }

  async keyLabelMaps(companyId?: string, programTemplateId?: string) {
    const params = new URLSearchParams();
    if (companyId) {
      params.set('company-id', companyId);
    }
    if (programTemplateId) {
      params.set('program-template-id', programTemplateId);
    }

    const queryString = params.toString();
    const url = this.buildURL('search') + `/key-label-maps${queryString ? `?${queryString}` : ''}`;
    return this.ajax(url, 'GET');
  }

  async downloadCsv(query: Record<string, unknown>) {
    const headers = {
      ...this.headers,
      'Content-Type': 'application/json', // sending JSON body
      Accept: 'text/csv', // expect CSV response
    };

    const url = this.buildURL('search') + `.csv`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(query),
    });

    if (response.ok) {
      const contentDisposition = response.headers.get('Content-Disposition');
      const name = contentDisposition?.split('filename=')[1]?.split('.')[0];
      const extension = contentDisposition?.split('.')[1]?.split(';')[0];
      const filename = `${name}.${extension}`.replaceAll('"', '');

      downloadFile(await response.blob(), filename, 'text/csv');
    }
  }

  async exportReport(
    reportId: string,
    options?: {
      companyId?: string;
      searchBody?: Record<string, unknown>;
      modelType?: string;
      columnKeys?: string[];
      columnLabels?: Record<string, string>;
      columnFormats?: Record<string, unknown>;
    }
  ): Promise<ReportExportResponse> {
    const url = `${this.host}/${this.namespace}/reports/${reportId}/export`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...this.headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...(options?.companyId ? { company_id: options.companyId } : {}),
        ...(options?.searchBody ? { search_body: options.searchBody } : {}),
        ...(options?.modelType ? { model_type: options.modelType } : {}),
        ...(options?.columnKeys ? { column_keys: options.columnKeys } : {}),
        ...(options?.columnLabels ? { column_labels: options.columnLabels } : {}),
        ...(options?.columnFormats ? { column_formats: options.columnFormats } : {}),
      }),
    });

    if (!response.ok) {
      const body = await response.json();
      throw new Error(body.error || `Export failed (${response.status})`);
    }

    const result = (await response.json()) as ReportExportResponse;

    // Trigger download from the signed URL
    const fileResponse = await fetch(result.signed_url);
    if (fileResponse.ok) {
      const blob = await fileResponse.blob();
      downloadFile(blob, result.filename, 'text/csv');
    }

    return result;
  }

  urlForQuery() {
    // Customize the endpoint for the `SearchPerson` model
    return `${this.host}/${this.namespace}`;
  }

  /**
    This method is called for every response that the adapter receives from the
    API. If the response has a 401 or 403 status code it invalidates the session (see
    @method handleResponse
    @param {Number} status The response status as received from the API
    @param  {Object} headers HTTP headers as received from the API
    @param {Any} payload The response body as received from the API
    @param {Object} requestData the original request information
    @protected
  */
  handleResponse(
    status: number,
    headers: Record<string, string>,
    payload: Record<string, unknown>,
    requestData: RequestData
  ) {
    this.ensureResponseAuthorized(status, headers, payload, requestData);

    return super.handleResponse(status, headers, payload, requestData);
  }

  /**
     The default implementation for handleResponse.
     If the response has a 401 or 403 status code it invalidates the session.
     Override this method if you want custom invalidation logic for incoming responses.
     @method ensureResponseAuthorized
     @param {Number} status The response status as received from the API
     @param  {Object} headers HTTP headers as received from the API
     @param {Any} payload The response body as received from the API
     @param {Object} requestData the original request information
    */
  ensureResponseAuthorized(
    status: number,
    headers: unknown,
    payload: unknown,
    requestData: unknown
  ) {
    if ([401, 403].includes(status) && this.session.isAuthenticated) {
      const userId = (this.sessionContext.user as { id?: string | number } | undefined)?.id;

      console.error('ensureResponseAuthorized caught error', {
        status,
        headers,
        payload,
        requestData,
        userId,
      });

      addError(`ensureResponseAuthorized caught error status ${status} for user ${userId}`, {
        status,
        headers,
        payload,
        requestData,
        userId,
      });
      this.session.invalidate();
    }
  }
}
